// Path: src/components/aols/privacy/OlsmePrivacy.tsx
// Updated to fix the double footer issue by removing the local Footer and layout wrapper.
// This change standardizes the component with the other updated privacy pages.
// Provided complete file content as requested.
// 06/07/25 10:55 AM +07
import React, { useCallback, Fragment } from "react";
import styled from "styled-components";
import { Typography } from "antd";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import logo from "/logo.jpg";
// REMOVED: Footer import is no longer needed in this component.
// REMOVED: Unused Layout import from 'antd'.
import { HomeOutlined } from "@ant-design/icons";

const { Title, Paragraph, Text } = Typography;

// Define props interface
interface OlsmePrivacyProps {}

// Styled components
// REMOVED: FullHeightLayout is no longer needed.

const BreadcrumbWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 20px;
  z-index: 1001;

  .ant-breadcrumb a {
    color: #ffcc00;
    transition: color 0.3s ease;
  }

  .ant-breadcrumb a:hover {
    color: #ffaa00;
  }

  @media (max-width: 768px) {
    left: 10px;
    top: 5px;
    .ant-breadcrumb {
      font-size: 12px;
    }
  }
`;

const CustomBreadcrumb = styled.div`
  margin-bottom: 20px;
  text-align: left;
  font-size: 14px;
  line-height: 1.5714285714285714;

  .breadcrumb-item {
    display: inline-block;
  }

  .breadcrumb-link {
    color: #ffaa00 !important; /* Sunlight orange for active links */
    text-decoration: none;
  }

  .breadcrumb-link:hover {
    color: #fce248 !important; /* Sunlight yellow on hover */
  }

  .breadcrumb-separator {
    color: #fff; /* White separator for contrast */
    margin: 0 8px;
  }

  .breadcrumb-non-active {
    color: #fff !important; /* White for non-active items */
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px;
  background: #111;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  color: #fff;

  @media (max-width: 768px) {
    padding: 20px;
    margin: 10px;
  }
`;

const LogoImg = styled.img`
  width: 150px;
  height: auto;
  margin-bottom: 30px;
  cursor: pointer;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(255, 204, 0, 0.3);
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    width: 120px;
  }
`;

const MainTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 0;
  margin-bottom: 20px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 5px;
  }
`;

interface StyledLinkProps {
  $isButton?: boolean;
}

const MainTitleButton = styled(Link)<StyledLinkProps>`
  background: #ffaa00;
  color: #0427f4;
  font-size: 24px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 6px;
  text-decoration: none;
  transition: background 0.3s ease, transform 0.3s ease;

  &:hover {
    background: #ffcc00;
    color: #0427f4;
    transform: scale(1.05);
  }

  &:focus {
    outline: 2px solid #ffcc00;
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    font-size: 18px;
    padding: 6px 12px;
  }
`;

const LinkStyled = styled.a`
  color: #ffaa00;
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover {
    color: #ffcc00;
    text-decoration: underline;
  }

  &:focus {
    outline: 2px solid #ffcc00;
    outline-offset: 2px;
  }
`;

const MainTitleText = styled(Title)`
  color: #ffffff !important;
  margin: 0 !important;
  font-size: 24px;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const SectionTitle = styled(Title)`
  color: #ffffff !important;
  margin-top: 30px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const SubsectionTitle = styled(Title)`
  color: #ffffff !important;
  margin-top: 20px;
  margin-bottom: 15px;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const PolicyText = styled(Paragraph)`
  line-height: 1.8;
  color: #ddd;
  margin-bottom: 15px;
  white-space: pre-wrap;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const List = styled.ul`
  margin-left: 20px;
  list-style-type: disc;

  li {
    margin: 5px 0;
    color: #ddd;

    @media (max-width: 768px) {
      font-size: 0.9rem;
    }
  }
`;

const OlsmePrivacy: React.FC<OlsmePrivacyProps> = () => {
  const handleLogoClick = useCallback(() => {
    window.location.href = "https://www.olsme.com";
  }, []);

  return (
    // UPDATED: Using React.Fragment instead of FullHeightLayout
    <Fragment>
      <Helmet>
        <title>OLSME Subscription Privacy Policy - OneLightSystem OLS</title>
        <meta
          name="description"
          content="Read the OLSME Subscription Privacy Policy for OneLightSystem OLS. Learn about the management, usage, and security of OLSME subscriptions in our ecosystem."
        />
        <meta
          name="keywords"
          content="OLSME Subscription, Privacy Policy, Sun Light Meditation, OLS Education, Light Civilization, OneLightSystem OLS"
        />
        <meta name="author" content="OneLightSystem OLS" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="OLSME Subscription Privacy Policy - OneLightSystem OLS" />
        <meta
          property="og:description"
          content="Explore the OLSME Subscription Privacy Policy for OneLightSystem OLS, detailing the management, usage, and security of OLSME subscriptions."
        />
        <meta property="og:url" content="https://www.olsme.com/olsme-privacy" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.olsme.com/logo.jpg" />
        <meta property="og:image:alt" content="OneLightSystem OLS Logo" />
        <meta property="og:site_name" content="OneLightSystem OLS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="OLSME Subscription Privacy Policy - OneLightSystem OLS" />
        <meta
          name="twitter:description"
          content="Learn about the OLSME Subscription Privacy Policy for OneLightSystem OLS, including management, usage, and security of OLSME subscriptions."
        />
        <meta name="twitter:image" content="https://www.olsme.com/logo.jpg" />
        <meta name="twitter:image:alt" content="OneLightSystem OLS Logo" />
        <meta name="twitter:site" content="@onelightsystem" />
        <meta name="twitter:creator" content="@onelightsystem" />
        <link rel="canonical" href="https://www.olsme.com/olsme-privacy" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "OLSME Subscription Privacy Policy - OneLightSystem OLS",
            description:
              "OLSME Subscription Privacy Policy for OneLightSystem OLS, detailing the management, usage, and security of OLSME subscriptions.",
            url: "https://www.olsme.com/OlsmePrivacy",
            publisher: {
              "@type": "Organization",
              name: "OneLightSystem OLS",
              logo: "https://www.olsme.com/logo.jpg",
              contactPoint: {
                "@type": "ContactPoint",
                email: "info@olsme.com",
                contactType: "Customer Service",
              },
              address: {
                "@type": "PostalAddress",
                streetAddress: "2033 Ralston Ave",
                addressLocality: "Belmont",
                addressRegion: "CA",
                postalCode: "94002",
                addressCountry: "US",
              },
              founder: {
                "@type": "Person",
                name: "Nazar Pankiv",
                jobTitle: "CEO",
              },
              sameAs: [
                "https://www.olsme.com",
                "https://www.ols-med.net",
                "https://twitter.com/onelightsystem",
                "https://www.facebook.com/onelightsystemols",
                "https://www.linkedin.com/company/one-light-system",
                "https://www.youtube.com/@ONELIGHTSYSTEMOLSMeditation",
              ],
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "https://www.olsme.com",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "OLSME Privacy",
                  item: "https://www.olsme.com/OlsmePrivacy",
                },
              ],
            },
          })}
        </script>
      </Helmet>
      <BreadcrumbWrapper>
        <CustomBreadcrumb>
          <span className="breadcrumb-item">
            <Link to="/" className="breadcrumb-link">
              <HomeOutlined /> Home
            </Link>
          </span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-item">
            <Link to="/privacy" className="breadcrumb-link">
              Main Privacy
            </Link>
          </span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-item breadcrumb-non-active">
            OLSME Privacy
          </span>
        </CustomBreadcrumb>
      </BreadcrumbWrapper>
      <ContentWrapper>
        <LinkStyled href="https://www.olsme.com" aria-label="Go to OneLightSystem OLS homepage">
          <LogoImg src={logo} alt="OneLightSystem OLS Logo" onClick={handleLogoClick} />
        </LinkStyled>
        <MainTitleWrapper>
          <MainTitleButton to="/privacy" aria-label="Go to Privacy page">
            onelightsystem
          </MainTitleButton>
          <MainTitleButton to="/OLSaeo" aria-label="Go to OLS iee.aeo page">
            OLS iee.aeo
          </MainTitleButton>
          <MainTitleButton to="/olsme" aria-label="Go to OLSME subscription page">
            OLSME subscription
          </MainTitleButton>
          <MainTitleText level={1}>
            Privacy Disclosure
          </MainTitleText>
        </MainTitleWrapper>

        <PolicyText>
          <strong>OneLightSystem OLS</strong>
          <br />
          OLSME Subscription Privacy for OneLightSystem OLS
          <br />
          Version: 8.0.1
          <br />
          Last Updated: April 08, 2025
          <br />
          © OneLightSystem OLS, LLC | OLS iee.aeo
          <br />
          CEO: Nazar Pankiv
        </PolicyText>

        <SectionTitle level={2}>
          Introduction
        </SectionTitle>
        <PolicyText>
          Welcome to the OLSME Subscription Policy for OneLightSystem OLS (hereinafter referred to
          as "OLS"). OLSME®© (OneLightSystem Meditative Energy) is a registered trademark and
          represents a unique invented technology that allows users to connect to OLSM meditation
          in a passenger mode through the OLS platform at{" "}
          <LinkStyled href="https://www.olsme.com" aria-label="Visit olsme.com">
            www.olsme.com
          </LinkStyled>. This policy outlines the management, distribution, usage, and security of
          OLSME subscriptions and its own private, undisclosed OLSME main concept, which operates
          on a Private and Protected model within the OneLightSystem ecosystem.
        </PolicyText>

        <SectionTitle level={2}>
          1. OLSME Subscription
        </SectionTitle>
        <PolicyText>
          OLSME platform powered by OLS human development technology. Personalized OLS Blockchain
          running on OLSystemMeditation routine engine and accessible Public extension from
          OneLightSystemMeditation engine.
          <br />
          <strong>Principle:</strong> OneLightSystem OLS Meditation Academy OLS iee.aeo invented
          OLSME for development, unifying Light Civilization.
          <br />
          <strong>Service:</strong> OLSME subscribers can accumulate autonomous inner energy
          equilibrium (IEF - Inner Energy Formation) by synchronizing with OLSMeditation. OLSME
          subscribers will be able to develop self-awareness, become more conscious, and achieve
          deeper meditation.
        </PolicyText>

        <SectionTitle level={2}>
          2. OLSME Subscription Management
        </SectionTitle>
        <PolicyText>
          OLSME subscribers can manage their subscription through the OLSME platform.
        </PolicyText>
        <SubsectionTitle level={3}>
          2.1 OLSME Subscriber Usage
        </SubsectionTitle>
        <List>
          <li>
            Register at{" "}
            <LinkStyled
              href="https://www.olsme.com/register/olsmeregister"
              aria-label="Register at olsmeregister"
            >
              https://www.olsme.com/register/olsmeregister
            </LinkStyled>.
          </li>
          <li>
            Verify your identity at{" "}
            <LinkStyled href="https://www.olsme.com/profile" aria-label="Visit profile page">
              olsme.com/profile
            </LinkStyled>.
          </li>
          <li>Review and sign the NDA (Non-Disclosure Agreement).</li>
          <li>First-time subscribers require a 1-year subscription to receive OLSME service.</li>
          <li>After the initial subscription, renewal is possible for 30 days (fees chart for 2025 applies).</li>
        </List>

        <SectionTitle level={2}>
          3. OLSME Subscription Sessions Usage Data Compliance
        </SectionTitle>
        <SubsectionTitle level={3}>
          3.1 OLSME User Subscriber Registration Fees Assessment Evaluation Process
        </SubsectionTitle>
        <List>
          <li>
            OLSME new registered users will receive an email from{" "}
            <LinkStyled href="mailto:info@olsme.com" aria-label="Email info@olsme.com">
              info@olsme.com
            </LinkStyled>{" "}
            with evaluated monthly fees based on age, location, and GDP (olsme.com automatic
            evaluation is under development).
          </li>
        </List>
        <SubsectionTitle level={3}>
          3.2 OLSME User Collects OLSME's
        </SubsectionTitle>
        <List>
          <li>OLSME's can be redeemed to apply to become an OLS student (intro & initiation).</li>
          <li>OLSME's can be used for re-activation fees.</li>
          <li>
            If a user doesn't generate any OLSME's during a 30-day period, the user will receive an
            email notification.
          </li>
        </List>
        <SubsectionTitle level={3}>
          3.3 Deactivation Requests, Cancellation, and Deletion
        </SubsectionTitle>
        <List>
          <li>
            OLSME users can deactivate their account anytime by submitting a deactivation request,
            valid for 30 days.
          </li>
          <li>
            OLSME users can cancel their account at any time. If the user has collected OLSME's,
            they will remain on the account for 30 days in case of re-activation, or the user can
            request to hold them for up to 2 years with a service fee of 1% of the total
            subscription.
          </li>
          <li>
            Refunds are not available; any leftover balance can be credited into OLS services only
            (e.g., OLS student fees, OLSLM assets).
          </li>
        </List>

        <SectionTitle level={2}>
          4. Information Collection
        </SectionTitle>
        <PolicyText>
          We collect information from you when you register for an OLSME subscription, use our
          services, or interact with us. The information we collect may include:
        </PolicyText>
        <List>
          <li>
            <strong>Personal Information:</strong> Name, email address, phone number, and other
            contact details.
          </li>
          <li>
            <strong>Payment Information:</strong> Credit card details and billing address.
          </li>
          <li>
            <strong>Usage Data:</strong> Information about how you use our services, including IP
            address, browser type, and device information.
          </li>
          <li>
            <strong>Cookies:</strong> We may use cookies and similar technologies to enhance your
            experience on our platform.
          </li>
        </List>

        <SectionTitle level={2}>
          5. Information Usage
        </SectionTitle>
        <PolicyText>
          We use the information we collect for the following purposes:
        </PolicyText>
        <List>
          <li>To provide and maintain our services.</li>
          <li>To process your transactions and manage your subscriptions.</li>
          <li>
            To communicate with you regarding your account, updates, and promotional offers.
          </li>
          <li>To improve our services and enhance user experience.</li>
          <li>To comply with legal obligations and protect our rights.</li>
        </List>

        <SectionTitle level={2}>
          6. Information Sharing
        </SectionTitle>
        <PolicyText>
          We do not sell or rent your personal information to third parties. However, we may share
          your information in the following circumstances:
        </PolicyText>
        <List>
          <li>
            With service providers who assist us in operating our platform and providing our
            services.
          </li>
          <li>With affiliates and subsidiaries within the OLS ecosystem.</li>
          <li>In response to legal requests or to protect our rights and the rights of others.</li>
          <li>In connection with a merger, acquisition, or sale of all or a portion of our business.</li>
        </List>

        <SectionTitle level={2}>
          7. Data Security
        </SectionTitle>
        <PolicyText>
          We take the security of your personal information seriously and implement appropriate
          technical and organizational measures to protect it from unauthorized access, loss, or
          misuse. However, please note that no method of transmission over the internet or
          electronic storage is 100% secure.
        </PolicyText>

        <SectionTitle level={2}>
          8. Your Rights
        </SectionTitle>
        <PolicyText>
          You have the right to access, correct, or delete your personal information. You may also
          have the right to object to or restrict the processing of your information. To exercise
          these rights, please contact us using the contact details provided below.
        </PolicyText>

        <SectionTitle level={2}>
          9. Changes to This Policy
        </SectionTitle>
        <PolicyText>
          We may update this policy from time to time. Any changes will be posted on this page with
          an updated effective date. We encourage you to review this policy periodically for any
          updates.
        </PolicyText>

        <SectionTitle level={2}>
          10. Contact Us
        </SectionTitle>
        <PolicyText>
          If you have any questions or concerns about this policy or our privacy practices, please
          contact us at:
          <br />
          <strong>OneLightSystem OLS</strong>
          <br />
          OLS iee.aeo
          <br />
          Email:{" "}
          <LinkStyled href="mailto:info@olsme.com" aria-label="Email info@olsme.com">
            info@olsme.com
          </LinkStyled>
        </PolicyText>
      </ContentWrapper>
      {/* REMOVED: The <Footer /> component is no longer rendered here. */}
    </Fragment>
  );
};

export default OlsmePrivacy;